/*
---
covers:
  - frontend/src/pages/platform/login.tsx
  - frontend/src/pages/producto/home.tsx
  - frontend/src/pages/sgsst/resumen/business-home.tsx
  - frontend/src/pages/empresa/mi_espacio/member-home.tsx
  - frontend/src/pages/sgsst/resumen/ruta.tsx
  - frontend/src/components/app-layout.tsx
  - frontend/src/components/sidebar-link.tsx
  - frontend/src/components/protected-route.tsx
revisado: 2026-09-23
---
*/
import type {ManualSeccion} from '../types'

export const seccion: ManualSeccion = {
  id: 'entrar-y-moverte',
  titulo: 'Entrar y moverte',
  resumen:
    'Cómo iniciar sesión con tu cédula y contraseña (no correo), y cómo se organiza la navegación de la app una vez adentro.',
  enCorto: [
    'Cómo se organiza la aplicación por dentro',
    'Ubique el menú lateral y su sección',
    'Cada pantalla tiene su propio capítulo acá',
  ],
  replayId: 'entrar-y-moverte',
  operaciones: [
    {
      id: 'iniciar-sesion',
      titulo: 'Iniciar sesión con tu cédula y contraseña',
      como: 'En la pantalla de «Iniciar sesión», ingresa tu «Cédula de ciudadanía (CC)» y tu «Contraseña», y usa el botón «Iniciar sesión». Si la olvidaste, el link «¿Olvidó su contraseña?» te manda a pedir un reinicio.',
      porQueImporta:
        'Kairos no usa correo para entrar — tu cédula es tu identidad ante la plataforma, la misma con la que tu empresa te registró; así entran incluso las personas sin correo.',
    },
    {
      id: 'aterrizar-en-tu-empresa',
      titulo: 'Entender dónde aterrizas al entrar',
      como: 'Kairos te lleva directo a la última empresa que visitaste en este dispositivo (o a tu única empresa, si solo tienes una) — nunca a una lista vacía. Como trabajador aterrizas en tu Resumen: te saluda por tu nombre y debajo van, en tarjetas, solo las cosas que dependen de ti — reportar un accidente, lo que te falta de tu registro, tus solicitudes, tus tareas del plan y tus soportes. Si en algún momento perteneces a varias empresas, el menú de tu nombre (arriba a la derecha) tiene «Cambiar de empresa».',
      porQueImporta:
        'Como un mismo trabajador puede estar vinculado a varias empresas, Kairos siempre te ubica trabajando en una — te ahorra el paso de escoger cada vez que entras.',
    },
    {
      id: 'leer-tu-resumen',
      titulo: 'Leer el Resumen según quién eres',
      como: 'Si operas el SG-SST, el Resumen se arma según tu papel, sin que tengas que elegir nada. Como dueño o representante legal ves «¿estoy protegido?»: el medidor de cumplimiento del año con su banda, el ciclo PHVA al lado (pasa el puntero por una barra y te dice cuántos puntos ganaste de cuántos), el plan de mejoramiento en una dona, la batería psicosocial, la accidentalidad y la exposición a multas — y arriba, «Decisiones que lo esperan», con lo que solo tú puedes firmar. Como responsable del SG-SST ves «¿qué me toca hoy?»: tu cola de trabajo en una tabla (qué vence, de quién es, cuándo, y un enlace que abre esa acción exacta), más el mapa de peligros, el registro legal y el avance del plan de trabajo. Si eres dueño y además tienes licencia SST verificada, ves las dos cosas en dos pestañas: abre en «Trabajo», tu cola operativa; «Protección» tiene la banda estratégica.',
      porQueImporta:
        'El dueño y el profesional del SG-SST miran el mismo sistema con preguntas distintas: uno quiere saber si está protegido, el otro qué le toca hacer hoy. Una sola pantalla para los dos obligaba a cada uno a saltarse la mitad. Y como la lente sale de tu rol y de tu licencia, nadie tiene que configurarla.',
    },
    {
      id: 'moverte-por-mi-espacio',
      titulo: 'Moverte por «Mi espacio» en el menú lateral',
      como: 'El menú lateral agrupa tus páginas bajo el rótulo «MI ESPACIO»: «Mi información» (tu ficha y tu perfil sociodemográfico), «Mis tareas del plan» (las acciones del plan de mejoramiento a tu cargo) y «Mis soportes y solicitudes» (lo que has subido y lo que tu empresa te ha pedido).',
      porQueImporta:
        'Como trabajador solo ves tu propio espacio — nunca el módulo completo del SG-SST que opera tu empresa —, así que el menú lateral te muestra exactamente lo tuyo y nada más, sin ítems que no puedes usar.',
    },
    {
      id: 'perfil-de-cargo-en-el-menu',
      titulo: 'Encontrar el perfil de cargo y lo que exige',
      como: 'En el menú del SG-SST hay cuatro entradas nuevas: «Perfiles de cargo» (Kairos arma el perfil de cada cargo con lo que ya sabe y tú lo confirmas), «Obras y proyectos» (a quién asignas a cada obra y quién puede entrar hoy), «IPS y solicitudes» (las solicitudes de examen que Kairos genera y envía, y los conceptos que vuelven) y «Cuarentena» (los archivos con datos clínicos que no entran al expediente). Como trabajador, «Mi espacio» suma «Lo que me piden»: todo lo que tu cargo te exige, con su estado.',
      porQueImporta:
        'Desde septiembre de 2026 todo requisito de una persona —curso, examen, documento— sale de su perfil de cargo, así que el menú te lleva primero al perfil y desde ahí a lo que falta, en vez de a listas sueltas.',
    },
    {
      id: 'plegar-secciones-del-menu',
      titulo: 'Plegar las secciones del menú lateral',
      como: 'Cada rótulo del menú lateral («EMPRESA», «SG-SST», «MI ESPACIO») es un botón: haz clic en él y la sección se pliega, dejando solo el rótulo; vuelve a hacer clic y se despliega. Kairos recuerda lo que dejaste plegado la próxima vez que entres. Todo arranca desplegado, y si navegas a una página que quedó dentro de una sección plegada, esa sección se abre sola.',
      porQueImporta:
        'El menú del SG-SST es largo porque el sistema de gestión lo es. Plegar lo que no estás usando esta semana te deja el menú corto sin perder nada — y como la sección donde estás parado se abre sola, nunca te quedas sin ver dónde estás.',
    },
  ],
}
